import { computed } from 'vue';
import { isNative, useConfigHelper } from '@ilteducation/config';
import { appToLogo, iltIcon, type AppNames } from '@ilteducation/components';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
// @ts-ignore // no types available,
import html2pdf from 'html2pdf.js';

export type { AppNames };

/**
 * jsPDF's `worker.save()` downloads through an anchor element, which the native apps' WebView silently ignores,
 * so there the PDF is handed to the OS share sheet instead.
 */
const savePdf = async (worker: any, fileName: string): Promise<void> => {
  if (!isNative()) {
    await worker.save();
    return;
  }

  const dataUri: string = await worker.output('datauristring');
  // Usernames end up in the file name, so anything that would be read as a path has to go.
  const path = fileName.replace(/[/\\:]/g, '_');

  await Filesystem.writeFile({ path, data: dataUri.split('base64,')[1], directory: Directory.Cache });
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });

  try {
    await Share.share({ title: path, url: uri, dialogTitle: path });
  } catch (error) {
    const userDismissedShareSheet = (error as Error).message === 'Share canceled';
    if (!userDismissedShareSheet) {
      throw error;
    }
  } finally {
    // The PDF holds login credentials, so the temporary copy must not outlive the share sheet.
    await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined);
  }
};

export const usePrintLoginCard = (accessibleApps: string[], appUrl: string) => {
  const logos = accessibleApps.length > 0 ? accessibleApps : ['ilteducation'];
  const { getAppUrl } = useConfigHelper();

  const appLogos = computed(() => logos.map((app) => appToLogo[app as AppNames]));
  const fullAppUrl = computed(() => (appUrl === '' ? getAppUrl('ilteducation') : appUrl));

  /**
   * Download a single page as a PDF.
   * @param selector The selector of the page to download.
   * @param filePostfix The postfix for the file name.
   * @param asPage Whether to download the page as an A4 page.
   * @returns A promise that resolves when the download is complete.
   */
  const download = async (selector: string, filePostfix: string, asPage: boolean = false): Promise<void> => {
    const element = document.getElementById(selector);

    if (!element) {
      console.error(`could not find element ${selector}`);
      return;
    }

    const pdfFileName = `login-card-${filePostfix}.pdf`;

    // @ts-ignore // no types available,
    let worker = html2pdf()
      // @ts-ignore // no types available,
      .set({
        filename: pdfFileName,
        html2canvas: {
          scale: 3,
        },
        jsPDF: {
          unit: 'mm',
          format: asPage ? 'a4' : [209.3 / 3, 296.6 / 3], // 1/3 ratio of A4
          orientation: 'portrait',
        },
      })
      .from(element)
      .toPdf();

    await savePdf(worker, pdfFileName);

    worker = null;
    return;
  };

  /**
   * Download multiple docoment as a single PDF.
   * @param pageClassName The CSS class name of the page elements to download.
   * @param fileName The name for the file name. XXX.pdf or login-cards.pdf default name
   * @returns A promise that resolves when the download is complete.
   */
  const downloadDocsAsPDF = async (
    pageClassName: string,
    fileName: string | undefined = 'login_cards',
  ): Promise<void> => {
    const pdfFileName = `${fileName.trim().replace(/\s/g, '_')}.pdf`;
    const pages = Array.from(document.getElementsByClassName(pageClassName));

    if (!pages) {
      console.error(`could not find elements with class '${pageClassName}'`);
      return;
    }

    // @ts-ignore // no types available,
    let worker = html2pdf()
      // @ts-ignore // no types available,
      .set({
        filename: pdfFileName,
        html2canvas: {
          scale: 3.5,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        },
      })
      .from(pages[0])
      .toPdf();
    pages.slice(1).forEach(function (page) {
      worker = worker
        .get('pdf')
        .then(function (pdf: any) {
          pdf.addPage();
        })
        .from(page)
        .toContainer()
        .toCanvas()
        .toImg()
        .toPdf();
    });

    await savePdf(worker, pdfFileName);

    worker = null;
    pages.length = 0;
    return;
  };

  return {
    appLogos,
    download,
    downloadDocsAsPDF,
    fullAppUrl,
    iltIcon,
  };
};
